import React, {FC, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {AvailabilityStatusTypesEnum} from '../../../../store/data/teammates/availabilityStatusSelectors';
import {AvatarBadgeStatus} from '../../../common/avatars/avatarBadgeStatus';
import {TeammateAvatarButton} from '../../../common/avatars/teammateAvatarButton';
import {topBarTooltipCustomMargin, topBarTooltipPreventOverflowPadding} from '../topBarHelpers';
import {findTeammateAvailabilityColor} from './userProfileHelpers';

/*
 * Props.
 */

interface UserProfileButtonProps {
  isDropdownOpen: boolean;
  avatarRef: RefObject<HTMLDivElement>;
  teammate: TeammateModel;
  availabilityStatus: AvailabilityStatusTypesEnum;
  isConnected: boolean;
}

/*
 * Styles.
 */

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  // We're stretched to fit the height of the top bar, so that the dropdown and tooltip are consistent with
  // other top bar buttons. The actual avatar button is smaller, so vertically center it.
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const UserProfileButton: FC<UserProfileButtonProps> = ({
  isDropdownOpen,
  avatarRef,
  teammate,
  availabilityStatus,
  isConnected,
}) => {
  const theme = useUserTheme();
  const availabilityColor = isConnected
    ? theme.palette[findTeammateAvailabilityColor(availabilityStatus)].shade40
    : theme.greys.shade60;

  return (
    <StyledTooltipCoordinator
      render={() => <Trans id="topbar-account">Account</Trans>}
      customMargin={topBarTooltipCustomMargin}
      preventOverflowPadding={topBarTooltipPreventOverflowPadding}
      hideOnMouseDown
    >
      <TeammateAvatarButton
        ref={avatarRef}
        teammate={teammate}
        size={24}
        hasHover={isDropdownOpen}
        hasDarkerHover
        isOpen={isDropdownOpen}
        // @TODO: Reimplement smoke test.
        // For our Ghost Inspector production smoke test.
        // interactionId="user-avatar"
      >
        <AvatarBadgeStatus
          teammate={teammate}
          size={VisualSizesEnum.MEDIUM}
          color={availabilityColor}
          borderColor={theme.greys.black}
          availabilityStatus={availabilityStatus}
        />
      </TeammateAvatarButton>
    </StyledTooltipCoordinator>
  );
};
